import { z } from "zod";
import type { CustomField } from "../schemas/custom-fields";

// ============================================================================
// Doctype Admin Epic (#347) A7 (#355) — client-side Zod schema builder.
//
// This module is a PURE helper — no DB, no React, no DOM. It converts the
// stored `core_custom_fields` rows into a `z.ZodObject` that the frontend
// can layer on top of a module's base form schema via `.extend()`. Lives in
// `services/` (not `ui/`) so both frontend and backend can call it.
//
// RELATIONSHIP TO `custom-field.service.ts#buildZodSchema`
// ----------------------------------------------------------------------------
// The service file already ships a `buildZodSchema()` that the ROUTE layer
// uses to validate inbound JSON (see `validateCustomData` in that file).
// That helper is optimized for server validation:
//   - forgiving `.optional().nullable()` on non-required fields (matches the
//     JSON-null payload shape the API actually receives)
//   - no min-length constraint on text (server doesn't care about empty
//     strings — the UI does)
//
// This file targets the FORM layer. Requirements differ:
//   - `required` fields must REJECT empty strings (`""`) and `null`, not
//     just `undefined`. Zod's default `.optional()` only strips `undefined`,
//     so a "required" text field would otherwise pass with an empty input.
//   - Each field should produce the tightest type achievable (e.g.
//     `z.enum([...options])` for selects when options exist) so downstream
//     `z.infer<typeof schema>` gives callers rich types.
//   - `date` uses a YYYY-MM-DD regex — HTML `<input type="date">` always
//     returns that shape and the backend stores it as-is.
//
// We deliberately keep BOTH helpers. Renaming/merging them would force a
// server-side behaviour change (currently tolerated by every existing API
// consumer) outside A7's scope.
//
// CALLERS
// ----------------------------------------------------------------------------
// - `<DoctypeForm>` — composes `baseSchema.extend(buildCustomFieldSchema(...))`
// - Unit tests — `modules/core/__tests__/custom-field-schema.test.ts`
// - Exposed to consumer modules via `@kubdee/sdk` re-export (see
//   `packages/sdk/src/platform-services.ts`)
// ============================================================================

/**
 * YYYY-MM-DD — the exact format produced by `<input type="date">` and the
 * shape the custom-field renderer already passes through verbatim. We
 * intentionally avoid `z.string().datetime()` (which requires an ISO-8601
 * timestamp with time component) — the stored data has no time part.
 */
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse the stored `options` JSON blob safely. Returns `null` if the JSON
 * is invalid or missing. Both `selectOptions` and `linkDoctype` live in
 * this blob (see the legacy admin UI + A6's CustomFieldsTab).
 */
export interface ParsedFieldOptions {
  selectOptions?: string[];
  linkDoctype?: string;
}

export function parseFieldOptions(
  raw: string | null | undefined,
): ParsedFieldOptions | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const out: ParsedFieldOptions = {};
    const candidate = parsed as Record<string, unknown>;
    if (Array.isArray(candidate.selectOptions)) {
      out.selectOptions = candidate.selectOptions.filter(
        (v): v is string => typeof v === "string",
      );
    }
    if (typeof candidate.linkDoctype === "string") {
      out.linkDoctype = candidate.linkDoctype;
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Build a single-field validator. Exported for testability — callers using
 * the form layer should prefer `buildCustomFieldSchema()` which also
 * assembles the wrapping `z.object({})`.
 */
export function buildFieldValidator(field: CustomField): z.ZodTypeAny {
  // Start with the base type-specific validator in its "value is present"
  // form. We apply required/optional handling uniformly at the end.
  let base: z.ZodTypeAny;

  switch (field.fieldType) {
    case "text": {
      let textSchema = z.string();
      if (field.maxValue) {
        const max = Number(field.maxValue);
        if (Number.isFinite(max) && max > 0) {
          textSchema = textSchema.max(max);
        }
      }
      if (field.minValue) {
        const min = Number(field.minValue);
        if (Number.isFinite(min) && min > 0) {
          textSchema = textSchema.min(min);
        }
      }
      if (field.pattern) {
        try {
          textSchema = textSchema.regex(new RegExp(field.pattern));
        } catch {
          // A bad regex from the admin UI must not crash the form. We
          // fall back to the un-regex'd schema. The admin UI has its own
          // pattern validation at authoring time; this is a safety net.
        }
      }
      base = textSchema;
      break;
    }

    case "number": {
      let numSchema = z.number().finite();
      if (field.minValue) {
        const min = Number(field.minValue);
        if (Number.isFinite(min)) {
          numSchema = numSchema.min(min);
        }
      }
      if (field.maxValue) {
        const max = Number(field.maxValue);
        if (Number.isFinite(max)) {
          numSchema = numSchema.max(max);
        }
      }
      base = numSchema;
      break;
    }

    case "currency": {
      // Currency is a number with a nonnegative floor unless the admin
      // explicitly overrides via minValue. We intentionally DON'T default
      // to `nonnegative()` when a `minValue` is configured — a credit-note
      // custom field may legitimately allow negatives.
      let currencySchema = z.number().finite();
      if (field.minValue) {
        const min = Number(field.minValue);
        if (Number.isFinite(min)) {
          currencySchema = currencySchema.min(min);
        }
      } else {
        currencySchema = currencySchema.nonnegative();
      }
      if (field.maxValue) {
        const max = Number(field.maxValue);
        if (Number.isFinite(max)) {
          currencySchema = currencySchema.max(max);
        }
      }
      base = currencySchema;
      break;
    }

    case "date": {
      // Pinned to the HTML `<input type="date">` output shape. Invalid
      // formats (including ISO timestamps with time) are rejected so the
      // server doesn't need to re-parse.
      base = z
        .string()
        .regex(DATE_REGEX, "date must be in YYYY-MM-DD format");
      break;
    }

    case "select": {
      const parsed = parseFieldOptions(field.options);
      const options = parsed?.selectOptions ?? [];
      if (options.length > 0) {
        // `z.enum` requires a non-empty tuple at the type level. We
        // assert because the length check above guarantees non-empty.
        base = z.enum(options as [string, ...string[]]);
      } else {
        // Graceful degrade: a select with no options acts like free text.
        // This happens when the admin is still authoring the field.
        base = z.string();
      }
      break;
    }

    case "checkbox": {
      base = z.boolean();
      break;
    }

    case "link": {
      // Link fields hold another doctype's id. We require a non-empty
      // string when the field is required (empty link = no selection).
      base = z.string().min(1);
      break;
    }

    default: {
      // Unknown `fieldType` from the DB (e.g. a future type not yet known
      // to the client). Accept any string so stale clients don't break on
      // newer server responses.
      base = z.string();
    }
  }

  // ─── Required vs optional handling ─────────────────────────────────────
  //
  // We treat a field as "filled" when the value is defined, non-null, and
  // (for strings) non-empty. The common Zod idiom `.optional()` only
  // strips `undefined`; we need to also accept `null` AND — for strings —
  // treat `""` as "not filled" so required text fields actually require
  // input. We use a preprocess step for that.
  if (field.isRequired) {
    // Keep the validator strict but accept `null → rejection` explicitly
    // so the caller sees a clean "required" error rather than a confusing
    // "expected string, got null" one.
    return z.preprocess((value) => {
      if (value === null || value === undefined) return undefined;
      // For strings, an empty-string submission is a "missing" input.
      if (typeof value === "string" && value.trim().length === 0) {
        return undefined;
      }
      return value;
    }, base);
  }

  // Optional fields: accept `undefined`, `null`, and empty-string as
  // "not filled" and return `undefined` downstream. That way the form's
  // final payload doesn't include keys for fields the user didn't touch.
  return z.preprocess(
    (value) => {
      if (value === null || value === undefined) return undefined;
      if (typeof value === "string" && value.trim().length === 0) {
        return undefined;
      }
      return value;
    },
    base.optional(),
  );
}

/**
 * Build a `z.ZodObject` whose shape mirrors the given custom-field
 * definitions. The resulting schema can be layered onto a base form
 * schema via `.extend()`:
 *
 * ```ts
 * const schema = baseSchema.extend(buildCustomFieldSchema(fields).shape);
 * ```
 *
 * (We expose the raw `shape` because Zod's `.extend(otherObject)` expects
 * an object, not a ZodObject — see the Zod docs.)
 */
export function buildCustomFieldSchema(
  fields: CustomField[],
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    shape[field.fieldName] = buildFieldValidator(field);
  }
  return z.object(shape);
}

/**
 * Derive the initial form state for custom fields from an existing record's
 * `custom_data` blob. Handles three shapes the DB may return:
 *   - `string` — JSON-encoded blob (the form on-wire shape)
 *   - `object` — already-parsed payload
 *   - `null` / `undefined` — no values yet (create mode or legacy row)
 *
 * Returns an empty object for any malformed blob so the form stays
 * usable even if the backend ever writes garbage.
 */
export function parseCustomData(
  raw: unknown,
): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    if (raw.trim().length === 0) return {};
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

/**
 * Compute the default value for a single field. Used by `<DoctypeForm>` to
 * seed the internal form state in create mode. Edit mode uses
 * `parseCustomData()` and falls back to this only for fields missing from
 * `custom_data`.
 */
export function getFieldDefault(field: CustomField): unknown {
  if (field.defaultValue == null || field.defaultValue === "") {
    // Checkboxes default to `false` so the DOM state is deterministic.
    // Other fields stay undefined — React will render them as empty.
    if (field.fieldType === "checkbox") return false;
    return undefined;
  }
  switch (field.fieldType) {
    case "number":
    case "currency": {
      const parsed = Number(field.defaultValue);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case "checkbox": {
      // Stored as "true"/"false" strings in the DB.
      return field.defaultValue === "true" || field.defaultValue === "1";
    }
    default:
      return field.defaultValue;
  }
}
